const NEAR_LIMIT_NUMERATOR = 3;
const NEAR_LIMIT_DENOMINATOR = 4;

export type AlertLimitTone = 'empty' | 'ok' | 'near' | 'full';

export function alertLimitTone(used: number, max: number): AlertLimitTone {
    if (used <= 0) return 'empty';
    if (used >= max) return 'full';
    if (used * NEAR_LIMIT_DENOMINATOR >= max * NEAR_LIMIT_NUMERATOR) {
        return 'near';
    }
    return 'ok';
}

export function countedAlertUnits(
    usedElsewhere: number,
    selectedCount: number,
): number {
    return usedElsewhere + selectedCount;
}

export function spendingSourceCount(
    selectedSourceIds: readonly string[],
    liveSourceIds: ReadonlySet<string> | null,
): number {
    if (liveSourceIds === null) {
        return selectedSourceIds.length;
    }
    let count = 0;
    for (const sourceId of selectedSourceIds) {
        if (liveSourceIds.has(sourceId)) {
            count += 1;
        }
    }
    return count;
}

export function activationExceedsAlertUnitLimit(
    usedElsewhere: number,
    ruleSourceCount: number,
    maxAlertUnits: number | null | undefined,
): boolean {
    if (maxAlertUnits === undefined) return true;
    if (maxAlertUnits === null) return false;
    return usedElsewhere + ruleSourceCount > maxAlertUnits;
}

function selectedSourcesStayOnRule(
    selectedSourceIds: readonly string[],
    savedSourceIds: readonly string[],
): boolean {
    const saved = new Set(savedSourceIds);
    return selectedSourceIds.every((sourceId) => saved.has(sourceId));
}

function sourcesAddedToRule(
    selectedSourceIds: readonly string[],
    savedSourceIds: readonly string[],
): number {
    const saved = new Set(savedSourceIds);
    let added = 0;
    for (const sourceId of selectedSourceIds) {
        if (!saved.has(sourceId)) {
            added += 1;
        }
    }
    return added;
}

export function selectionExceedsAllowedAlertUnits(
    usedElsewhere: number,
    selectedSpend: number,
    maxAlertUnits: number | null | undefined,
    savedSourceIds: readonly string[] | null,
    selectedSourceIds: readonly string[],
): boolean {
    if (
        !activationExceedsAlertUnitLimit(
            usedElsewhere,
            selectedSpend,
            maxAlertUnits,
        )
    ) {
        return false;
    }
    if (savedSourceIds === null) return true;
    return !selectedSourcesStayOnRule(selectedSourceIds, savedSourceIds);
}

export function remainingAlertSourceSlots(
    maxAlertUnits: number | null | undefined,
    usedElsewhere: number,
    selectedCount: number,
): number | null {
    if (maxAlertUnits === undefined) return 0;
    if (maxAlertUnits === null) return null;
    return Math.max(0, maxAlertUnits - usedElsewhere - selectedCount);
}

export type NextOverCapTooltip =
    | { kind: 'atCap' }
    | { kind: 'uncheck'; count: number };

export function nextOverCapTooltip(
    usedElsewhere: number,
    selectedSpend: number,
    maxAlertUnits: number | null | undefined,
    savedSourceIds: readonly string[] | null,
    selectedSourceIds: readonly string[],
): NextOverCapTooltip | null {
    if (typeof maxAlertUnits !== 'number') return null;
    if (
        !activationExceedsAlertUnitLimit(
            usedElsewhere,
            selectedSpend,
            maxAlertUnits,
        )
    ) {
        return null;
    }
    if (savedSourceIds !== null) {
        const added = sourcesAddedToRule(selectedSourceIds, savedSourceIds);
        if (added === 0) return null;
        return { kind: 'uncheck', count: added };
    }
    const overCapCount = usedElsewhere + selectedSpend - maxAlertUnits;
    if (usedElsewhere >= maxAlertUnits) {
        return { kind: 'atCap' };
    }
    return { kind: 'uncheck', count: overCapCount };
}

export function canCheckAlertSource(
    sourceId: string,
    usedElsewhere: number,
    selectedSourceIds: ReadonlySet<string>,
    maxAlertUnits: number | null | undefined,
    liveSourceIds: ReadonlySet<string> | null,
    savedSourceIds: readonly string[] | null = null,
): boolean {
    if (maxAlertUnits === undefined) return selectedSourceIds.has(sourceId);
    if (maxAlertUnits === null) return true;
    if (selectedSourceIds.has(sourceId)) return true;
    if (savedSourceIds?.includes(sourceId)) return true;
    return (
        usedElsewhere +
            countedSelectedSources(selectedSourceIds, liveSourceIds) <
        maxAlertUnits
    );
}

function countedSelectedSources(
    selectedSourceIds: ReadonlySet<string>,
    liveSourceIds: ReadonlySet<string> | null,
): number {
    if (liveSourceIds === null) {
        return selectedSourceIds.size;
    }
    let count = 0;
    selectedSourceIds.forEach((sourceId) => {
        if (liveSourceIds.has(sourceId)) {
            count += 1;
        }
    });
    return count;
}

export function selectAlertSourcesUntilLimit(
    usedElsewhere: number,
    selectedSourceIds: readonly string[],
    candidateSourceIds: readonly string[],
    maxAlertUnits: number | null | undefined,
    liveSourceIds: ReadonlySet<string> | null,
    savedSourceIds: readonly string[] | null = null,
): string[] {
    const next = new Set(selectedSourceIds);
    if (maxAlertUnits === undefined) {
        return Array.from(next);
    }
    if (maxAlertUnits === null) {
        for (const sourceId of candidateSourceIds) {
            next.add(sourceId);
        }
        return Array.from(next);
    }
    const saved = savedSourceIds ? new Set(savedSourceIds) : null;
    let liveSelectedCount = spendingSourceCount(
        selectedSourceIds,
        liveSourceIds,
    );
    for (const sourceId of candidateSourceIds) {
        if (next.has(sourceId)) {
            continue;
        }
        const restoresSavedSource = saved?.has(sourceId) ?? false;
        if (
            !restoresSavedSource &&
            usedElsewhere + liveSelectedCount >= maxAlertUnits
        ) {
            continue;
        }
        next.add(sourceId);
        if (liveSourceIds === null || liveSourceIds.has(sourceId)) {
            liveSelectedCount += 1;
        }
    }
    return Array.from(next);
}
